import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, QueryStatus, useToast } from "@cx/ui";
import type {
  CustomReportInput,
  CustomReportView,
  ReportCatalogColumn,
  ReportCatalogView,
  ReportDefinitionInput,
  ReportFileFormat,
  ReportResult,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import {
  REPORT_AGG_LABELS,
  REPORT_MAX_COLUMNS,
  REPORT_MAX_FILTERS,
  REPORT_MAX_SORT,
  REPORT_OP_LABELS,
  REPORT_PREVIEW_ROWS,
  reportCellText,
  type ReportOp,
  type ReportSource,
} from "@cx/domain";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import {
  buildDefinition,
  editorFromDefinition,
  opNeedsValue,
  type EditorFilter,
  type EditorState,
} from "./definition";

const NUMERIC = new Set(["int", "money", "qty", "pct"]);
const FORMAT_LABEL: Record<ReportFileFormat, string> = { csv: "CSV", xlsx: "Excel", pdf: "PDF" };

function emptyState(source: ReportSource, cols: readonly ReportCatalogColumn[]): EditorState {
  return { source, columns: cols.slice(0, 3).map((c) => ({ key: c.key, agg: "" })), filters: [], sort: [] };
}

/**
 * Construtor de relatórios (T3.6, RF-CAD-06): fonte → colunas (com resumo opcional; as colunas sem resumo agrupam) →
 * filtros → ordenação → prévia de até 100 linhas. Exportar gera CSV/Excel/PDF na fila (Ações assíncronas).
 */
export function CustomReportEditorPage(): ReactElement {
  const { id } = useParams({ strict: false });
  const { modelo } = useSearch({ strict: false }) as { modelo?: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const catalog = useQuery({
    queryKey: ["custom-reports", companyId, "catalog"],
    queryFn: () => api<ReportCatalogView>("/custom-reports/catalog"),
  });
  const saved = useQuery({
    queryKey: ["custom-reports", companyId, id],
    queryFn: () => api<CustomReportView>(`/custom-reports/${id}`),
    enabled: !!id,
  });

  const [state, setState] = useState<EditorState | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [shared, setShared] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Estado inicial: relatório salvo, modelo pronto (?modelo=) ou a primeira fonte do catálogo.
  useEffect(() => {
    if (state || !catalog.data) return;
    const colsOf = (s: ReportSource) => catalog.data.sources.find((x) => x.key === s)?.columns ?? [];
    if (id) {
      if (!saved.data) return;
      const d = saved.data;
      setState(editorFromDefinition(d.definition as ReportDefinitionInput, colsOf(d.source)));
      setName(d.name);
      setDescription(d.description);
      setShared(d.shared);
      return;
    }
    const preset = modelo ? catalog.data.presets.find((p) => p.id === modelo) : undefined;
    if (preset) {
      setState(
        editorFromDefinition(preset.definition as ReportDefinitionInput, colsOf(preset.definition.source)),
      );
      setName(preset.name);
      setDescription(preset.description);
      return;
    }
    const first = catalog.data.sources[0];
    if (first) setState(emptyState(first.key, first.columns));
  }, [catalog.data, saved.data, id, modelo, state]);

  const cols = useMemo(
    () => catalog.data?.sources.find((s) => s.key === state?.source)?.columns ?? [],
    [catalog.data, state?.source],
  );
  const colByKey = useMemo(() => new Map(cols.map((c) => [c.key, c])), [cols]);

  const definition = (): ReportDefinitionInput | null => {
    if (!state) return null;
    try {
      setFormError(null);
      return buildDefinition(state, cols);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Definição inválida.");
      return null;
    }
  };

  const run = useMutation({
    mutationFn: (v: { definition: ReportDefinitionInput; offset: number }) =>
      api<ReportResult>("/custom-reports/run", { method: "POST", body: JSON.stringify(v) }),
    onSuccess: setResult,
    onError: (e: Error) => setFormError(errorMessage(e)),
  });
  const save = useMutation({
    mutationFn: (body: CustomReportInput) =>
      id
        ? api<CustomReportView>(`/custom-reports/${id}`, { method: "PUT", body: JSON.stringify(body) })
        : api<CustomReportView>("/custom-reports", {
            method: "POST",
            body: JSON.stringify(body),
            headers: { "Idempotency-Key": crypto.randomUUID() },
          }),
    onSuccess: (r) => {
      toast("success", "Relatório salvo");
      qc.invalidateQueries({ queryKey: ["custom-reports", companyId] });
      if (!id) nav({ to: "/meu-negocio/relatorios/$id", params: { id: r.id } });
    },
    onError: (e: Error) => setFormError(errorMessage(e)),
  });
  const exportFile = useMutation({
    mutationFn: (v: { definition: ReportDefinitionInput; fileFormat: ReportFileFormat }) =>
      api<{ jobId: string }>("/custom-reports/export", {
        method: "POST",
        body: JSON.stringify({ ...v, name: name.trim() || "Relatório" }),
      }),
    onSuccess: (_r, v) => {
      toast(
        "info",
        `Exportação em ${FORMAT_LABEL[v.fileFormat]} na fila. Baixe em Ações assíncronas, no topo.`,
      );
      qc.invalidateQueries({ queryKey: ["jobs", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const archive = useMutation({
    mutationFn: () => api(`/custom-reports/${id}/archive`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Relatório arquivado");
      qc.invalidateQueries({ queryKey: ["custom-reports", companyId] });
      nav({ to: "/meu-negocio/relatorios" });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });

  if (catalog.isLoading || (id && saved.isLoading) || !state)
    return (
      <>
        <ContentHeader title="Relatório" breadcrumb={["Meu Negócio", "Relatórios"]} />
        <QueryStatus loading error={catalog.error ?? saved.error} />
        {catalog.data && catalog.data.sources.length === 0 && (
          <div className="alert alert-warning">Seu perfil não tem acesso a nenhuma fonte de relatório.</div>
        )}
      </>
    );

  const canSave = id ? (saved.data?.canEdit ?? false) : can(perms, "custom_reports", "create");
  const canExport = can(perms, "custom_reports", "export");
  const patch = (p: Partial<EditorState>) => setState({ ...state, ...p });
  const setFilter = (i: number, p: Partial<EditorFilter>) =>
    patch({ filters: state.filters.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  const move = <T,>(list: T[], i: number, d: number) => {
    const out = [...list];
    const j = i + d;
    if (j < 0 || j >= out.length) return out;
    [out[i], out[j]] = [out[j]!, out[i]!];
    return out;
  };
  const selectedCols = state.columns.filter((c) => colByKey.has(c.key));
  const sortOptions = selectedCols.map((c) => ({
    value: `${c.key}|${c.agg}`,
    label: `${colByKey.get(c.key)!.label}${c.agg ? ` (${REPORT_AGG_LABELS[c.agg]})` : ""}`,
  }));

  const doRun = (offset = 0) => {
    const d = definition();
    if (d) run.mutate({ definition: d, offset });
  };
  const doSave = () => {
    const d = definition();
    if (!d) return;
    if (!name.trim()) return setFormError("Informe o nome do relatório.");
    save.mutate({ name: name.trim(), description: description.trim(), shared, definition: d });
  };
  const doExport = (fileFormat: ReportFileFormat) => {
    const d = definition();
    if (d) exportFile.mutate({ definition: d, fileFormat });
  };

  return (
    <>
      <ContentHeader
        title={id ? name || "Relatório" : "Novo relatório"}
        breadcrumb={["Meu Negócio", "Relatórios", id ? "Editar" : "Novo"]}
        actions={
          <>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => doRun(0)}
              disabled={run.isPending}
            >
              {run.isPending ? "Rodando…" : "Visualizar"}
            </button>
            {canExport && (
              <div className="btn-group" role="group" aria-label="Exportar">
                {(["csv", "xlsx", "pdf"] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={exportFile.isPending}
                    onClick={() => doExport(f)}
                  >
                    Exportar {FORMAT_LABEL[f]}
                  </button>
                ))}
              </div>
            )}
            {canSave && (
              <button type="button" className="btn btn-primary" onClick={doSave} disabled={save.isPending}>
                Salvar
              </button>
            )}
            {id && canSave && (
              <button type="button" className="btn btn-outline-danger" onClick={() => setArchiveOpen(true)}>
                Arquivar
              </button>
            )}
          </>
        }
      />
      {formError && (
        <div className="alert alert-danger" role="alert">
          {formError}
        </div>
      )}
      <div className="box card p-3 mb-3">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label" htmlFor="rep-name">
              Nome
            </label>
            <input
              id="rep-name"
              className="form-control"
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="col-md-5">
            <label className="form-label" htmlFor="rep-desc">
              Descrição
            </label>
            <input
              id="rep-desc"
              className="form-control"
              maxLength={300}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label" htmlFor="rep-source">
              Fonte dos dados
            </label>
            <select
              id="rep-source"
              className="form-select"
              value={state.source}
              onChange={(e) => {
                const s = catalog.data!.sources.find((x) => x.key === e.target.value)!;
                setState(emptyState(s.key, s.columns));
                setResult(null);
              }}
            >
              {catalog.data!.sources.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          {canSave && (
            <div className="col-12">
              <div className="form-check">
                <input
                  id="rep-shared"
                  type="checkbox"
                  className="form-check-input"
                  checked={shared}
                  onChange={(e) => setShared(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="rep-shared">
                  Compartilhar com a equipe (cada pessoa vê só os dados que o perfil dela permite)
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row g-3 mb-3">
        <section className="col-lg-6" aria-labelledby="rep-cols">
          <div className="box card p-3 h-100">
            <h2 id="rep-cols" className="h6">
              Colunas
            </h2>
            <p className="small text-muted">
              Com algum resumo (soma, contagem, mínimo, máximo), as colunas sem resumo viram o agrupamento
              (até 3).
            </p>
            {state.columns.map((c, i) => {
              const spec = colByKey.get(c.key);
              return (
                <div key={i} className="d-flex gap-2 mb-2" data-testid="report-column">
                  <select
                    className="form-select form-select-sm"
                    aria-label={`Coluna ${i + 1}`}
                    value={c.key}
                    onChange={(e) =>
                      patch({
                        columns: state.columns.map((x, j) =>
                          j === i ? { key: e.target.value, agg: "" } : x,
                        ),
                      })
                    }
                  >
                    {cols.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 140 }}
                    aria-label={`Resumo da coluna ${i + 1}`}
                    value={c.agg}
                    onChange={(e) =>
                      patch({
                        columns: state.columns.map((x, j) =>
                          j === i
                            ? { ...x, agg: e.target.value as EditorState["columns"][number]["agg"] }
                            : x,
                        ),
                      })
                    }
                  >
                    <option value="">Sem resumo</option>
                    {(spec?.aggs ?? []).map((a) => (
                      <option key={a} value={a}>
                        {REPORT_AGG_LABELS[a]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={`Subir coluna ${i + 1}`}
                    onClick={() => patch({ columns: move(state.columns, i, -1) })}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    aria-label={`Descer coluna ${i + 1}`}
                    onClick={() => patch({ columns: move(state.columns, i, 1) })}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    aria-label={`Remover coluna ${i + 1}`}
                    disabled={state.columns.length === 1}
                    onClick={() =>
                      patch({
                        columns: state.columns.filter((_, j) => j !== i),
                        sort: state.sort.filter((s) => !(s.key === c.key && s.agg === c.agg)),
                      })
                    }
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary align-self-start"
              disabled={state.columns.length >= REPORT_MAX_COLUMNS || cols.length === 0}
              onClick={() => patch({ columns: [...state.columns, { key: cols[0]!.key, agg: "" }] })}
            >
              Adicionar coluna
            </button>
          </div>
        </section>

        <section className="col-lg-6" aria-labelledby="rep-filters">
          <div className="box card p-3 mb-3">
            <h2 id="rep-filters" className="h6">
              Filtros
            </h2>
            {state.filters.length === 0 && <p className="small text-muted">Sem filtros: todas as linhas.</p>}
            {state.filters.map((f, i) => (
              <FilterRow
                key={i}
                index={i}
                filter={f}
                cols={cols}
                onChange={(p) => setFilter(i, p)}
                onRemove={() => patch({ filters: state.filters.filter((_, j) => j !== i) })}
              />
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary align-self-start"
              disabled={state.filters.length >= REPORT_MAX_FILTERS || cols.length === 0}
              onClick={() =>
                patch({
                  filters: [
                    ...state.filters,
                    { key: cols[0]!.key, op: cols[0]!.ops[0]!, text: "", text2: "", multi: [] },
                  ],
                })
              }
            >
              Adicionar filtro
            </button>
          </div>
          <div className="box card p-3">
            <h2 className="h6">Ordenação</h2>
            {state.sort.map((s, i) => (
              <div key={i} className="d-flex gap-2 mb-2">
                <select
                  className="form-select form-select-sm"
                  aria-label={`Ordenar por ${i + 1}`}
                  value={`${s.key}|${s.agg}`}
                  onChange={(e) => {
                    const [key, agg] = e.target.value.split("|") as [
                      string,
                      EditorState["sort"][number]["agg"],
                    ];
                    patch({ sort: state.sort.map((x, j) => (j === i ? { ...x, key, agg } : x)) });
                  }}
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ maxWidth: 150 }}
                  aria-label={`Direção ${i + 1}`}
                  value={s.dir}
                  onChange={(e) =>
                    patch({
                      sort: state.sort.map((x, j) =>
                        j === i ? { ...x, dir: e.target.value as "asc" | "desc" } : x,
                      ),
                    })
                  }
                >
                  <option value="asc">Crescente</option>
                  <option value="desc">Decrescente</option>
                </select>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  aria-label={`Remover ordenação ${i + 1}`}
                  onClick={() => patch({ sort: state.sort.filter((_, j) => j !== i) })}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary align-self-start"
              disabled={state.sort.length >= REPORT_MAX_SORT || selectedCols.length === 0}
              onClick={() => {
                const c = selectedCols[0]!;
                patch({ sort: [...state.sort, { key: c.key, agg: c.agg, dir: "asc" }] });
              }}
            >
              Adicionar ordenação
            </button>
          </div>
        </section>
      </div>

      <section className="box card" aria-labelledby="rep-preview">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 pb-0">
          <h2 id="rep-preview" className="h6 mb-0">
            Prévia
          </h2>
          {result && (
            <span className="small text-muted" data-testid="report-total">
              {result.totalCapped ? "Mais de 100.000" : result.total.toLocaleString("pt-BR")} linha(s)
              {result.total > result.limit &&
                ` · mostrando ${(result.offset + 1).toLocaleString("pt-BR")}–${Math.min(
                  result.offset + result.limit,
                  result.total,
                ).toLocaleString("pt-BR")}`}
            </span>
          )}
        </div>
        {!result && (
          <p className="p-3 text-muted mb-0">
            Clique em Visualizar para ver até {REPORT_PREVIEW_ROWS} linhas.
          </p>
        )}
        {result && (
          <>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" aria-label="Prévia do relatório">
                <thead>
                  <tr>
                    {result.columns.map((c, i) => (
                      <th key={i} scope="col" className={NUMERIC.has(c.type) ? "text-end" : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.length === 0 && (
                    <tr>
                      <td colSpan={result.columns.length} className="text-muted text-center py-4">
                        Nenhuma linha com esses filtros.
                      </td>
                    </tr>
                  )}
                  {result.rows.map((row, r) => (
                    <tr key={r}>
                      {row.map((v, i) => (
                        <td key={i} className={NUMERIC.has(result.columns[i]!.type) ? "text-end" : undefined}>
                          {reportCellText(result.columns[i]!, v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {result.total > result.limit && (
              <div className="d-flex gap-2 p-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={result.offset === 0 || run.isPending}
                  onClick={() => doRun(Math.max(0, result.offset - result.limit))}
                >
                  Anteriores
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={result.offset + result.limit >= result.total || run.isPending}
                  onClick={() => doRun(result.offset + result.limit)}
                >
                  Próximas
                </button>
              </div>
            )}
          </>
        )}
      </section>
      <ConfirmDialog
        open={archiveOpen}
        title="Arquivar relatório"
        message={`Arquivar "${name}"? Ele sai da lista; o histórico fica na auditoria.`}
        confirmLabel="Arquivar"
        onConfirm={() => {
          archive.mutate();
          setArchiveOpen(false);
        }}
        onCancel={() => setArchiveOpen(false)}
      />
    </>
  );
}

function FilterRow({
  index,
  filter: f,
  cols,
  onChange,
  onRemove,
}: {
  index: number;
  filter: EditorFilter;
  cols: readonly ReportCatalogColumn[];
  onChange(p: Partial<EditorFilter>): void;
  onRemove(): void;
}): ReactElement {
  const col = cols.find((c) => c.key === f.key);
  const n = index + 1;
  const inputType = col?.type === "date" || col?.type === "datetime" ? "date" : "text";
  const valueInput = (value: string, set: (v: string) => void, label: string) =>
    col?.type === "bool" ? (
      <select
        className="form-select form-select-sm"
        aria-label={label}
        value={value}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Escolha</option>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </select>
    ) : col?.type === "enum" && col.options ? (
      <select
        className="form-select form-select-sm"
        aria-label={label}
        value={value}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Escolha</option>
        {Object.entries(col.options).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
    ) : (
      <input
        className="form-control form-control-sm"
        type={inputType}
        aria-label={label}
        inputMode={col && NUMERIC.has(col.type) ? "decimal" : undefined}
        maxLength={200}
        value={value}
        onChange={(e) => set(e.target.value)}
      />
    );
  return (
    <div className="d-flex flex-wrap gap-2 mb-2" data-testid="report-filter">
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 200 }}
        aria-label={`Filtro ${n}: coluna`}
        value={f.key}
        onChange={(e) => {
          const c = cols.find((x) => x.key === e.target.value)!;
          onChange({ key: c.key, op: c.ops[0]!, text: "", text2: "", multi: [] });
        }}
      >
        {cols.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>
      <select
        className="form-select form-select-sm"
        style={{ maxWidth: 160 }}
        aria-label={`Filtro ${n}: condição`}
        value={f.op}
        onChange={(e) => onChange({ op: e.target.value as ReportOp })}
      >
        {(col?.ops ?? []).map((o) => (
          <option key={o} value={o}>
            {REPORT_OP_LABELS[o]}
          </option>
        ))}
      </select>
      {opNeedsValue(f.op) && f.op === "in" && col?.type === "enum" && col.options ? (
        <div
          className="d-flex flex-wrap gap-2 align-items-center"
          role="group"
          aria-label={`Filtro ${n}: valores`}
        >
          {Object.entries(col.options).map(([k, v]) => (
            <label key={k} className="form-check-label small">
              <input
                type="checkbox"
                className="form-check-input me-1"
                checked={f.multi.includes(k)}
                onChange={(e) =>
                  onChange({ multi: e.target.checked ? [...f.multi, k] : f.multi.filter((x) => x !== k) })
                }
              />
              {v}
            </label>
          ))}
        </div>
      ) : opNeedsValue(f.op) ? (
        <div className="d-flex gap-2 flex-grow-1">
          {valueInput(f.text, (text) => onChange({ text }), `Filtro ${n}: valor`)}
          {f.op === "between" && valueInput(f.text2, (text2) => onChange({ text2 }), `Filtro ${n}: até`)}
        </div>
      ) : null}
      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        aria-label={`Remover filtro ${n}`}
        onClick={onRemove}
      >
        ×
      </button>
    </div>
  );
}
